import React, { useContext, useMemo, useState } from 'react';
import { CancelButton } from '../../../../../../VentoryUI/components/common/Button';
import { TextInput } from '../../../../../components/TextInput';
import { t } from '../../../../../../types/translation/Translator';
import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import { ProductContext } from '../../../../../../context/ProductContext';
import { Product } from '../../../../../../types/product';
import { CompanyContext } from '../../../../../../context/CompanyContext';
import { StockLocationContext } from '../../../../../../context/StockLocationContext';
import { ProductMasterDataContext } from '../../../../../../context/ProductMasterDataContext';
import { DropdownSearchSelect } from '../../../../../components/Dropdown';
import { HandlingUnitContext } from '../../../../../../context/HandlingUnitContext';
import ProductMasterDataDropdownItem from '../../../../../components/Dropdown/templates/ProductMasterData/ProductMasterDataDropdownItem';
import { HandlingUnit } from '../../../../../../types/handling_unit/handlingUnit';
import { BinContext } from '../../../../../../context/BinContext';
import { Bin } from '../../../../../../types/bin';
import useMutation from '../../../../../../hooks/useMutation';
import {
  BundleProductResponse,
  BundleProductVariables,
  ProductMutations,
} from '../../../../../../graphql/product.graphql';
import { useToast } from '../../../../../../providers/ToastProvider';
import { ToastType } from '../../../../../components/Toast';
import { Feedback } from '../../../../../components/Feedback/feedback';
import { Button, ButtonVariant } from '../../../../../components/Button';
import { ActionModalPane } from '../../../../../components/Modal';
import { UnitOfMeasure } from '../../../../../../types/uom';

interface QuickActionBundleViewProps {
  setOpen: (open: boolean) => void;
  pmdId: string;
  productId?: string;
}

export default function QuickActionBundleView({ setOpen, pmdId, productId }: QuickActionBundleViewProps) {
  const { showToast } = useToast();

  const { currentCompany } = useContext(CompanyContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { products } = useContext(ProductContext);
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { handlingUnits } = useContext(HandlingUnitContext);
  const { bins } = useContext(BinContext);

  const pmd = productMasterData.get(pmdId);

  const product = useMemo(() => {
    return new Product(
      products.get(pmdId)?.get(productId || '') || {
        companyId: currentCompany.id,
        productMasterDataId: pmdId,
        stockLocationId: [...stockLocations.values()][0].id,
        quantity: UnitOfMeasure.toQuantity(
          pmd?.unitOfMeasure,
          products.get(pmdId)?.get(productId || '')?.unitQuantity || '0',
        ),
      },
    );
  }, [productId, pmdId]);

  const [result, setResult] = useState<HandlingUnit | undefined>();
  const [destinationBin, setDestinationBin] = useState<Bin | undefined>(bins.get(product.binId));
  const [resultQuantity, setResultQuantity] = useState<string>('1');

  const getQuantity = (q: string = resultQuantity) => {
    if (!result || !q) return 0;

    return result.quantity * parseFloat(q);
  };

  const quantity = useMemo(() => {
    return getQuantity();
  }, [result, resultQuantity]);

  const destinationBins = useMemo(() => {
    return [...bins.values()].filter(item => item.stockLocationId === product.stockLocationId);
  }, [product]);

  const [bundleProduct, { loading }] = useMutation<BundleProductResponse, BundleProductVariables>(
    ProductMutations.bundleProduct,
    {
      onCompleted: res => {
        setOpen(false);
        showToast({
          type: ToastType.success,
          title: t(TranslationKey.handlingUnitBundleSuccessfullyToastTitle).singular.upper,
        });
      },
    },
  );

  const validate = () => {
    if (
      quantity > parseFloat(UnitOfMeasure.toQuantity(productMasterData.get(pmdId)?.unitOfMeasure, product.unitQuantity))
    )
      return false;
    if (quantity <= 0) return false;
    if (!result) return false;
    if (!destinationBin) return false;

    return true;
  };

  const handleBundle = () => {
    if (!result) return;

    bundleProduct({
      variables: {
        productId: product.id,
        productMasterDataId: result?.parentId,
        quantity: quantity,
        binId: destinationBin?.id,
      },
    });
  };

  const footer = (
    <div className='flex justify-end gap-2'>
      <CancelButton onClick={() => setOpen(false)} disabled={loading} />
      <Button
        variant={ButtonVariant.primary}
        text={t(TranslationKey.handlingUnitBundle).singular.upper}
        onClick={handleBundle}
        loading={loading}
        disabled={!validate()}
      />
    </div>
  );

  return (
    <ActionModalPane footer={footer}>
      <div className='grid grid-cols-2 gap-2'>
        <TextInput
          label={t(TranslationKey.handlingUnitBundle).singular.label}
          type='number'
          value={quantity}
          disabled={true}
        />
        <DropdownSearchSelect
          label={t(TranslationKey.handlingUnitBundleInto).singular.label}
          placeholder={t(TranslationKey.productReferenceData).singular.label}
          items={Array.from(handlingUnits.values()).filter(
            hu => hu.productMasterDataId === product.productMasterDataId,
          )}
          itemBuilder={item => (
            <ProductMasterDataDropdownItem productMasterData={productMasterData.get(item.parentId)} />
          )}
          textBuilder={item => productMasterData.get(item.parentId)?.productName || ''}
          searchBuilder={item => {
            const p = productMasterData.get(item.parentId);
            return `${p?.productName} ${p?.productNumber}`;
          }}
          value={result}
          onSelect={item => setResult(item)}
        />
        <TextInput
          label={t(TranslationKey.handlingUnitResult).singular.label}
          type='number'
          value={resultQuantity}
          disabled={!result}
          onChange={item => setResultQuantity(item)}
          feedback={item => {
            if (
              getQuantity(item) >
              parseFloat(UnitOfMeasure.toQuantity(productMasterData.get(pmdId)?.unitOfMeasure, product.unitQuantity))
            ) {
              return {
                type: Feedback.Type.error,
                text: t(TranslationKey.insufficientQuantityAvailableInBin).singular.label,
              };
            } else if (getQuantity(item) <= 0) {
              return {
                type: Feedback.Type.error,
                text: t(TranslationKey.handlingUnitQuantityError).singular.label,
              };
            }
          }}
        />
        <DropdownSearchSelect
          label={t(TranslationKey.destinationBin).singular.label}
          placeholder={t(TranslationKey.destinationBin).singular.label}
          items={destinationBins}
          textBuilder={item => bins.get(item.id)?.name || ''}
          value={destinationBin}
          onSelect={item => setDestinationBin(item)}
        />
      </div>
    </ActionModalPane>
  );
}
